// src/dom.ts
function classifyItem(item) {
  if ("type" in item && item.type === "separator") {
    return "separator";
  }
  if ("children" in item && item.children) {
    return "submenu";
  }
  return "action";
}
function createMenuPanel(zIndex, maxHeight) {
  const panel = document.createElement("div");
  panel.className = "ex0-menu";
  panel.style.position = "fixed";
  panel.style.zIndex = String(zIndex);
  if (maxHeight !== null) {
    panel.style.maxHeight = `${maxHeight}px`;
    panel.style.overflowY = "auto";
    panel.classList.add("ex0-menu--scrollable");
  }
  return panel;
}
function createMenuItem(item, index) {
  const el = document.createElement("div");
  el.className = "ex0-menu-item";
  el.setAttribute("data-index", String(index));
  if (item.disabled) {
    el.classList.add("ex0-menu-item--disabled");
  }
  const iconSpan = document.createElement("span");
  iconSpan.className = "ex0-menu-item-icon";
  if (item.icon) {
    const img = document.createElement("img");
    img.src = item.icon;
    img.alt = "";
    iconSpan.appendChild(img);
  }
  el.appendChild(iconSpan);
  const labelSpan = document.createElement("span");
  labelSpan.className = "ex0-menu-item-label";
  labelSpan.textContent = item.label;
  el.appendChild(labelSpan);
  const kind = classifyItem(item);
  if (kind === "action" && item.shortcut) {
    const shortcutSpan = document.createElement("span");
    shortcutSpan.className = "ex0-menu-item-shortcut";
    shortcutSpan.textContent = item.shortcut;
    el.appendChild(shortcutSpan);
  }
  if (kind === "submenu") {
    const arrowSpan = document.createElement("span");
    arrowSpan.className = "ex0-menu-item-arrow";
    arrowSpan.textContent = "\u25B8";
    el.appendChild(arrowSpan);
  }
  return el;
}
function createSeparator() {
  const el = document.createElement("div");
  el.className = "ex0-menu-separator";
  return el;
}

// src/keyboard.ts
function isNavigable(item) {
  const kind = classifyItem(item);
  if (kind === "separator") {
    return false;
  }
  if ("disabled" in item && item.disabled) {
    return false;
  }
  return true;
}
function getNextIndex(current, items, direction) {
  const len = items.length;
  if (len === 0) {
    return -1;
  }
  let idx = current;
  for (let i = 0; i < len; i++) {
    idx = (idx + direction + len) % len;
    if (isNavigable(items[idx])) {
      return idx;
    }
  }
  return -1;
}
function getFirstIndex(items) {
  for (let i = 0; i < items.length; i++) {
    if (isNavigable(items[i])) {
      return i;
    }
  }
  return -1;
}
function getLastIndex(items) {
  for (let i = items.length - 1; i >= 0; i--) {
    if (isNavigable(items[i])) {
      return i;
    }
  }
  return -1;
}

// src/positioning.ts
function computeMenuPosition(x, y, menuWidth, menuHeight, viewportWidth, viewportHeight) {
  let finalX = x;
  let finalY = y;
  let maxHeight = null;
  if (finalX + menuWidth > viewportWidth) {
    finalX = Math.max(0, finalX - menuWidth);
  }
  if (menuHeight > viewportHeight) {
    finalY = 0;
    maxHeight = viewportHeight;
  } else if (finalY + menuHeight > viewportHeight) {
    finalY = Math.max(0, finalY - menuHeight);
  }
  return { x: finalX, y: finalY, maxHeight };
}
function computeSubmenuPosition(parentRect, submenuWidth, submenuHeight, viewportWidth, viewportHeight, parentFlippedLeft) {
  let finalX;
  let finalY = parentRect.top;
  let maxHeight = null;
  if (parentFlippedLeft) {
    finalX = parentRect.left - submenuWidth;
  } else {
    finalX = parentRect.right;
  }
  if (finalX + submenuWidth > viewportWidth) {
    finalX = parentRect.left - submenuWidth;
  }
  if (finalX < 0) {
    finalX = parentRect.right;
    if (finalX + submenuWidth > viewportWidth) {
      finalX = 0;
    }
  }
  if (submenuHeight > viewportHeight) {
    finalY = 0;
    maxHeight = viewportHeight;
  } else if (finalY + submenuHeight > viewportHeight) {
    finalY = Math.max(0, viewportHeight - submenuHeight);
  }
  return { x: finalX, y: finalY, maxHeight };
}

// src/ContextMenu.ts
function isDisabled(item) {
  return "disabled" in item && item.disabled === true;
}
function createContextMenu(options) {
  let items = options.items;
  const zIndex = options.zIndex ?? 1e4;
  const container = options.container ?? document.body;
  const submenuDelay = options.submenuDelay ?? 200;
  let rootPanel = null;
  let openSubmenus = [];
  let activeIndex = -1;
  let activeDepth = 0;
  let submenuOpenTimer = null;
  let submenuCloseTimer = null;
  let destroyed = false;
  let lastAnchorX = 0;
  let lastAnchorY = 0;
  function onPointerDown(e) {
    const target = e.target;
    if (rootPanel && rootPanel.contains(target)) {
      return;
    }
    for (const sub of openSubmenus) {
      if (sub.element.contains(target)) {
        return;
      }
    }
    hide();
  }
  function onKeyDown(e) {
    if (!rootPanel) {
      return;
    }
    const currentItems = getItemsAtDepth(activeDepth);
    if (!currentItems) {
      return;
    }
    switch (e.key) {
      case "Escape": {
        if (activeDepth > 0) {
          closeSubmenusAbove(activeDepth - 1);
          activeDepth--;
          e.preventDefault();
        } else {
          hide();
          e.preventDefault();
        }
        break;
      }
      case "ArrowDown": {
        const next = getNextIndex(activeIndex, currentItems, 1);
        if (next !== -1) {
          setActiveItem(activeDepth, next);
        }
        e.preventDefault();
        break;
      }
      case "ArrowUp": {
        const prev = getNextIndex(activeIndex, currentItems, -1);
        if (prev !== -1) {
          setActiveItem(activeDepth, prev);
        }
        e.preventDefault();
        break;
      }
      case "ArrowRight": {
        if (activeIndex >= 0 && activeIndex < currentItems.length) {
          const item = currentItems[activeIndex];
          if (classifyItem(item) === "submenu" && !isDisabled(item)) {
            openSubmenuForIndex(activeDepth, activeIndex);
            const subItems = item.children;
            const first = getFirstIndex(subItems);
            activeDepth++;
            if (first !== -1) {
              setActiveItem(activeDepth, first);
            }
          }
        }
        e.preventDefault();
        break;
      }
      case "ArrowLeft": {
        if (activeDepth > 0) {
          closeSubmenusAbove(activeDepth - 1);
          activeDepth--;
          e.preventDefault();
        }
        break;
      }
      case "Enter":
      case " ": {
        if (activeIndex >= 0 && activeIndex < currentItems.length) {
          const item = currentItems[activeIndex];
          const kind = classifyItem(item);
          if (kind === "action" && !isDisabled(item)) {
            item.action();
            hide();
          } else if (kind === "submenu" && !isDisabled(item)) {
            openSubmenuForIndex(activeDepth, activeIndex);
            const subItems = item.children;
            const first = getFirstIndex(subItems);
            activeDepth++;
            if (first !== -1) {
              setActiveItem(activeDepth, first);
            }
          }
        }
        e.preventDefault();
        break;
      }
      case "Home": {
        const first = getFirstIndex(currentItems);
        if (first !== -1) {
          setActiveItem(activeDepth, first);
        }
        e.preventDefault();
        break;
      }
      case "End": {
        const last = getLastIndex(currentItems);
        if (last !== -1) {
          setActiveItem(activeDepth, last);
        }
        e.preventDefault();
        break;
      }
    }
  }
  function getItemsAtDepth(depth) {
    if (depth === 0) {
      return items;
    }
    const sub = openSubmenus.find((s) => s.depth === depth);
    if (!sub) {
      return null;
    }
    const parentItems = getItemsAtDepth(depth - 1);
    if (!parentItems) {
      return null;
    }
    const parentItem = parentItems[sub.parentIndex];
    if (classifyItem(parentItem) === "submenu") {
      return parentItem.children;
    }
    return null;
  }
  function getPanelAtDepth(depth) {
    if (depth === 0) {
      return rootPanel;
    }
    const sub = openSubmenus.find((s) => s.depth === depth);
    return sub ? sub.element : null;
  }
  function setActiveItem(depth, index) {
    const panel = getPanelAtDepth(depth);
    if (panel) {
      const prev = panel.querySelector(".ex0-menu-item--active");
      if (prev) {
        prev.classList.remove("ex0-menu-item--active");
      }
      const el = panel.querySelector(`[data-index="${index}"]`);
      if (el) {
        el.classList.add("ex0-menu-item--active");
      }
    }
    activeIndex = index;
  }
  function clearTimers() {
    if (submenuOpenTimer !== null) {
      clearTimeout(submenuOpenTimer);
      submenuOpenTimer = null;
    }
    if (submenuCloseTimer !== null) {
      clearTimeout(submenuCloseTimer);
      submenuCloseTimer = null;
    }
  }
  function closeSubmenusAbove(depth) {
    const toRemove = openSubmenus.filter((s) => s.depth > depth);
    for (const sub of toRemove) {
      sub.element.remove();
    }
    openSubmenus = openSubmenus.filter((s) => s.depth <= depth);
    if (depth >= 0) {
      const parentSub = openSubmenus.find((s) => s.depth === depth);
      if (parentSub) {
        activeIndex = parentSub.parentIndex;
      }
    }
  }
  function openSubmenuForIndex(parentDepth, itemIndex) {
    closeSubmenusAbove(parentDepth);
    const parentItems = getItemsAtDepth(parentDepth);
    if (!parentItems) {
      return;
    }
    const item = parentItems[itemIndex];
    if (classifyItem(item) !== "submenu") {
      return;
    }
    const submenuItems = item.children;
    const parentPanel = getPanelAtDepth(parentDepth);
    if (!parentPanel) {
      return;
    }
    const parentItemEl = parentPanel.querySelector(
      `[data-index="${itemIndex}"]`
    );
    if (!parentItemEl) {
      return;
    }
    const submenuPanel = createMenuPanel(zIndex + parentDepth + 1, null);
    buildMenuItems(submenuPanel, submenuItems, parentDepth + 1);
    container.appendChild(submenuPanel);
    const parentRect = parentItemEl.getBoundingClientRect();
    const submenuRect = submenuPanel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rootRect = rootPanel?.getBoundingClientRect();
    const parentFlippedLeft = rootRect ? parentPanel.getBoundingClientRect().left < rootRect.left : false;
    const pos = computeSubmenuPosition(
      { left: parentRect.left, right: parentRect.right, top: parentRect.top },
      submenuRect.width,
      submenuRect.height,
      vw,
      vh,
      parentFlippedLeft
    );
    submenuPanel.style.left = `${pos.x}px`;
    submenuPanel.style.top = `${pos.y}px`;
    if (pos.maxHeight !== null) {
      submenuPanel.style.maxHeight = `${pos.maxHeight}px`;
      submenuPanel.style.overflowY = "auto";
      submenuPanel.classList.add("ex0-menu--scrollable");
    }
    openSubmenus.push({
      element: submenuPanel,
      depth: parentDepth + 1,
      parentIndex: itemIndex
    });
  }
  function buildMenuItems(panel, menuItems, depth) {
    menuItems.forEach((item, index) => {
      const kind = classifyItem(item);
      if (kind === "separator") {
        panel.appendChild(createSeparator());
        return;
      }
      const el = createMenuItem(
        item,
        index
      );
      el.addEventListener("pointerenter", () => {
        if (submenuCloseTimer !== null) {
          clearTimeout(submenuCloseTimer);
          submenuCloseTimer = null;
        }
        activeDepth = depth;
        setActiveItem(depth, index);
        if (kind === "submenu" && !isDisabled(item)) {
          if (submenuOpenTimer !== null) {
            clearTimeout(submenuOpenTimer);
          }
          submenuOpenTimer = setTimeout(() => {
            openSubmenuForIndex(depth, index);
            submenuOpenTimer = null;
          }, submenuDelay);
        } else {
          if (submenuOpenTimer !== null) {
            clearTimeout(submenuOpenTimer);
            submenuOpenTimer = null;
          }
          closeSubmenusAbove(depth);
        }
      });
      el.addEventListener("pointerleave", () => {
        const hasOpenChild = openSubmenus.some(
          (s) => s.depth === depth + 1 && s.parentIndex === index
        );
        if (hasOpenChild) {
          submenuCloseTimer = setTimeout(() => {
            closeSubmenusAbove(depth);
            submenuCloseTimer = null;
          }, submenuDelay);
        }
      });
      el.addEventListener("click", () => {
        if (isDisabled(item)) {
          return;
        }
        if (kind === "action") {
          item.action();
          hide();
        }
      });
      panel.appendChild(el);
    });
  }
  function show(x, y) {
    if (destroyed) {
      return;
    }
    hide();
    lastAnchorX = x;
    lastAnchorY = y;
    rootPanel = createMenuPanel(zIndex, null);
    if (options.className) {
      rootPanel.classList.add(options.className);
    }
    buildMenuItems(rootPanel, items, 0);
    container.appendChild(rootPanel);
    const rect = rootPanel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pos = computeMenuPosition(x, y, rect.width, rect.height, vw, vh);
    rootPanel.style.left = `${pos.x}px`;
    rootPanel.style.top = `${pos.y}px`;
    if (pos.maxHeight !== null) {
      rootPanel.style.maxHeight = `${pos.maxHeight}px`;
      rootPanel.style.overflowY = "auto";
      rootPanel.classList.add("ex0-menu--scrollable");
    }
    activeIndex = -1;
    activeDepth = 0;
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    options.onShow?.();
  }
  function hide() {
    if (!rootPanel) {
      return;
    }
    clearTimers();
    for (const sub of openSubmenus) {
      sub.element.remove();
    }
    openSubmenus = [];
    rootPanel.remove();
    rootPanel = null;
    activeIndex = -1;
    activeDepth = 0;
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("keydown", onKeyDown);
    options.onHide?.();
  }
  function isVisible() {
    return rootPanel !== null;
  }
  function updateItems(newItems) {
    items = newItems;
    if (rootPanel) {
      hide();
      show(lastAnchorX, lastAnchorY);
    }
  }
  function destroy() {
    hide();
    destroyed = true;
  }
  return { show, hide, isVisible, updateItems, destroy };
}
export {
  createContextMenu
};
